// Alton Valley — how much time a person has, day by day.
//
// One answer, used by every screen that asks "can they take this on": the
// plan's Resource Load and Plan Portfolio, Resource Management, PM Allocation,
// the Planner, Hours Overview and the Executive Summary's utilisation. Before
// this each of them had its own idea (headcount x 5, Monday to Friday, weekdays
// x 7.5 x FTE, 37.5 a week) and they disagreed about the same person.
//
// A person's day, in order:
//   1. Outside their employment (before they started, after they left): none.
//   2. A weekend, or a weekday outside the working days HR recorded: none.
//   3. A bank holiday (England and Wales, where everybody works), or a company
//      closure for everybody or for one of their teams: none.
//   4. Otherwise their contracted hours for that day, less any approved leave,
//      by the half day.
//
// Contracted hours are expected_weekly_hours spread over their working days,
// or over Monday to Friday where none are recorded. One exception: when weekly
// hours still carry the full-time default (37.5) but FTE says part time, FTE
// is believed, because that is the field HR has evidently changed.
//
// Days are standard days of 7.5 hours, so a full-timer has 1 a day and a
// three-day-a-week person has 1 on each of their three days.
//
// Pure functions, no D1, so the arithmetic is testable on its own.

import { workingWeekdays } from '../../frontend/src/utils/leaveDays.js';
import { toUTC, toISO, addCalendarDays } from './schedule.js';

export const STANDARD_DAY_HOURS = 7.5;
export const FULL_TIME_WEEK_HOURS = 37.5;

const round2 = (n) => Math.round(n * 100) / 100;

/* ────────────────────────────────────────────────────────────
   Contracted week
   ──────────────────────────────────────────────────────────── */

/**
 * A person's contracted weekly hours, where the figure came from, and whether
 * their fields disagree (worth showing, because it is the kind of thing nobody
 * notices until a plan is wrong).
 */
export function contractedWeek(person = {}) {
  const fte = Number(person.fte) > 0 ? Number(person.fte) : 1;
  const ewh = Number(person.expected_weekly_hours);
  const dayHours = Number(person.capacity_hours_per_day) > 0 ? Number(person.capacity_hours_per_day) : STANDARD_DAY_HOURS;
  const fromFte = round2(fte * 5 * dayHours);

  let hours;
  let source;
  if (ewh > 0 && !(ewh === FULL_TIME_WEEK_HOURS && fte < 1)) {
    hours = ewh;
    source = 'weekly_hours';
  } else {
    hours = fromFte;
    source = 'fte';
  }

  const pattern = workingWeekdays(person.working_days);
  const notes = [];
  if (ewh > 0 && Math.abs(ewh - fte * FULL_TIME_WEEK_HOURS) > 0.5) {
    notes.push(source === 'fte'
      ? `Weekly hours say ${ewh}h but FTE is ${fte}; FTE was used (${hours}h).`
      : `Weekly hours (${ewh}h) and FTE (${fte}) disagree; weekly hours were used.`);
  }
  if (pattern && fte < 1 && Math.abs(pattern.size / 5 - fte) > 0.01) {
    notes.push(`Works ${pattern.size} day${pattern.size === 1 ? '' : 's'} a week but FTE is ${fte}.`);
  }
  return { hours: round2(hours), source, pattern, notes };
}

/** The hours a person is contracted for on one date, before holidays or leave. */
export function contractedDayHours(person, date, week = contractedWeek(person)) {
  const dow = new Date(toUTC(date)).getUTCDay();          // 0 = Sunday
  if (week.pattern) return week.pattern.has(dow) ? week.hours / week.pattern.size : 0;
  return dow === 0 || dow === 6 ? 0 : week.hours / 5;
}

/* ────────────────────────────────────────────────────────────
   Context: holidays, closures and leave, indexed once
   ──────────────────────────────────────────────────────────── */

/**
 * Index everything that takes days away, so asking about a person's day is a
 * set lookup rather than a scan.
 *
 * @param {object}   input
 * @param {string[]} input.bankHolidays   YYYY-MM-DD, already filtered to England and Wales
 * @param {object[]} input.closures       { holiday_date, team_id }  team_id null = everybody
 * @param {object[]} input.leave          { person_id, start_date, end_date, status, start_half, end_half, leave_type }
 * @param {object}   input.teamsByPerson  person_id -> [team_id, ...] (primary and additional)
 */
export function makeCapacityContext({ bankHolidays = [], closures = [], leave = [], teamsByPerson = {} } = {}) {
  const holidays = new Set(bankHolidays);
  const everybodyClosed = new Set();
  const teamClosed = new Map();            // team_id -> Set(date)
  for (const c of closures) {
    if (!c?.holiday_date) continue;
    if (!c.team_id) { everybodyClosed.add(c.holiday_date); continue; }
    if (!teamClosed.has(c.team_id)) teamClosed.set(c.team_id, new Set());
    teamClosed.get(c.team_id).add(c.holiday_date);
  }

  // person_id -> Map(date -> { fraction, leave_type }). A half day is 0.5.
  const leaveByPerson = new Map();
  for (const l of leave) {
    if (!l?.person_id || !l.start_date) continue;
    if (l.status && l.status !== 'approved') continue;
    if (!leaveByPerson.has(l.person_id)) leaveByPerson.set(l.person_id, new Map());
    const days = leaveByPerson.get(l.person_id);
    const end = l.end_date || l.start_date;
    let d = l.start_date;
    let guard = 0;
    while (toUTC(d) <= toUTC(end) && guard++ < 800) {
      let fraction = 1;
      // An afternoon start or a morning finish is half a day off that day.
      if (d === l.start_date && l.start_half === 'pm') fraction = 0.5;
      if (d === end && l.end_half === 'am') fraction = 0.5;
      const was = days.get(d)?.fraction || 0;
      days.set(d, { fraction: Math.min(1, was + fraction), leave_type: l.leave_type || 'leave' });
      d = addCalendarDays(d, 1);
    }
  }

  return { holidays, everybodyClosed, teamClosed, leaveByPerson, teamsByPerson };
}

/* ────────────────────────────────────────────────────────────
   One person, one day
   ──────────────────────────────────────────────────────────── */

function employmentBounds(person) {
  return {
    start: person.start_of_employment || person.employment_start || null,
    end: person.end_of_employment || person.employment_end || person.date_of_leaving || null,
  };
}

/**
 * A person's day.
 *
 * @returns {{ contracted: number, available: number, leave: number, reason: string|null }}
 *   in hours. `contracted` is what they would normally work that day (used to
 *   size their share of a task), `available` is what is left after holidays,
 *   closures and leave, `leave` is the part taken by leave. `reason` names why
 *   a contracted day is not fully available: 'not_employed', 'non_working',
 *   'bank_holiday', 'closure', 'leave', 'half_leave'.
 */
export function personDay(person, date, ctx, week = contractedWeek(person)) {
  const { start, end } = employmentBounds(person);
  if ((start && date < start) || (end && date > end)) {
    return { contracted: 0, available: 0, leave: 0, reason: 'not_employed' };
  }
  const contracted = contractedDayHours(person, date, week);
  if (contracted <= 0) return { contracted: 0, available: 0, leave: 0, reason: 'non_working' };
  if (ctx?.holidays?.has(date)) return { contracted, available: 0, leave: 0, reason: 'bank_holiday' };
  if (ctx?.everybodyClosed?.has(date)) return { contracted, available: 0, leave: 0, reason: 'closure' };
  const teams = ctx?.teamsByPerson?.[person.id] || (person.team_id ? [person.team_id] : []);
  if (teams.some(t => ctx?.teamClosed?.get(t)?.has(date))) {
    return { contracted, available: 0, leave: 0, reason: 'closure' };
  }
  const off = ctx?.leaveByPerson?.get(person.id)?.get(date);
  if (off) {
    const leave = contracted * off.fraction;
    return { contracted, available: contracted - leave, leave, reason: off.fraction >= 1 ? 'leave' : 'half_leave' };
  }
  return { contracted, available: contracted, leave: 0, reason: null };
}

/**
 * Available hours over a date range, inclusive, with a breakdown of what took
 * the rest. For the figures that are a single number per person and period:
 * Hours Overview's expected hours, the Executive Summary's capacity.
 */
export function capacityInRange(person, from, to, ctx) {
  const week = contractedWeek(person);
  const out = {
    contracted_hours: 0, available_hours: 0, leave_hours: 0,
    bank_holiday_hours: 0, closure_hours: 0, notes: week.notes,
  };
  let d = from;
  let guard = 0;
  while (toUTC(d) <= toUTC(to) && guard++ < 4000) {
    const day = personDay(person, d, ctx, week);
    out.contracted_hours += day.contracted;
    out.available_hours += day.available;
    out.leave_hours += day.leave;
    if (day.reason === 'bank_holiday') out.bank_holiday_hours += day.contracted;
    if (day.reason === 'closure') out.closure_hours += day.contracted;
    d = addCalendarDays(d, 1);
  }
  for (const k of ['contracted_hours', 'available_hours', 'leave_hours', 'bank_holiday_hours', 'closure_hours']) {
    out[k] = round2(out[k]);
  }
  return out;
}

/** Every date from `from` to `to` inclusive, as YYYY-MM-DD. */
export function eachDay(from, to) {
  const out = [];
  let d = from;
  let guard = 0;
  while (toUTC(d) <= toUTC(to) && guard++ < 4000) { out.push(d); d = addCalendarDays(d, 1); }
  return out;
}

/** First and last day of the calendar month a YYYY-MM date names. */
export function monthBounds(ym) {
  const [y, m] = ym.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${ym}-01`, to: `${ym}-${String(last).padStart(2, '0')}` };
}

export { toISO };

/**
 * Days of a person's time a task takes at a share: on each of the plan's
 * working days from start to end, their contracted fraction of a standard day
 * times the share. The rule the load engine and the Resourcing tab both use,
 * so a three-day-a-week person at 100% on a five-day task is three days.
 */
export function personDaysOnTask(person, start, end, share, calendar) {
  if (!person || !start || !calendar) return 0;
  const week = contractedWeek(person);
  const ctx = makeCapacityContext();
  let total = 0;
  let d = start;
  let guard = 0;
  while (toUTC(d) <= toUTC(end || start) && guard++ < 1500) {
    if (calendar.isWorkingDay(d)) total += (personDay(person, d, ctx, week).contracted / STANDARD_DAY_HOURS) * share;
    d = addCalendarDays(d, 1);
  }
  return total;
}
